<template>
    <div>
        <div v-if="canDisplayMap">
            <h6
                class="map-selection"
                v-if="useIn !== 'show-assignments'">
                {{
                    this.useIn === 'country-picker'
                        ? `Gekozen land: ${selectedCountry ?? 'Nog geen land gekozen.'}`
                        : `Gekozen continent: ${selectedContinent ?? 'Nog geen continent gekozen.'}`
                }}
            </h6>
            <div
                class="map-container"
                id="svgContainer"
                ref="mapContainer">
                <svg
                    ref="svgMap"
                    :viewBox="`0 0 ${viewportWidth} ${viewportHeight}`">
                    <!-- SVG will be loaded here dynamically using D3 -->
                </svg>
                <div
                    class="map-tooltip tooltip"
                    ref="tooltip"
                    :class="{ visible: tooltipVisible }"
                    :style="tooltipStyle">
                    {{ tooltipContent }}
                </div>
            </div>
        </div>
        <div v-if="!canDisplayMap">
            <div class="alert alert-danger">Er is geen continent geselecteerd voor deze categorie.</div>
            <button
                class="btn btn-primary"
                @click="setupContinent">
                Kies een continent
            </button>
        </div>
    </div>
</template>

<script>
import * as d3 from 'd3';

const unselectedMapElementColor = '#ccd0d0';
const circularProgressBarRadius = 40;
const defaultSelectionColor = '#07A1FF';

export default {
    name: 'design-2021-worldmap-component',
    props: {
        useIn: {
            type: String,
            required: false,
            default: '',
        },
        continentsIn: {
            type: Object,
            required: false,
            default: null,
        },
        countriesIn: {
            type: Object,
            required: false,
            default: null,
        },
        selectedContinentIn: {
            type: String,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            svg: null,
            g: null,
            zoom: null,
            tooltipVisible: false,
            tooltipContent: '',
            tooltipStyle: {
                left: '0px',
                top: '0px',
            },
            shouldShowCountries: false,
            viewportWidth: 1010,
            viewportHeight: 666,
            scaleFactor: 1,
            continents: null,
            countries: null,
            boundingClientRect: null,
            selectedCountry: null,
            selectedContinent: null,
        };
    },
    mounted() {
        this.continents = this.continentsIn;
        this.countries = this.countriesIn;
        if (this.canDisplayMap) {
            this.initializeMap();
        }
    },

    methods: {
        async initializeMap() {
            // Load the SVG file
            const svgData = await d3.xml('/images/worldmap/world_grouped.svg');
            this.g =
                this.useIn === 'country-picker'
                    ? d3
                          .select(svgData)
                          .select('#' + this.selectedContinentIn)
                          .node()
                    : d3.select(svgData).select('g').node();

            if (this.g) {
                this.svg = d3.select(this.$refs.svgMap);
                this.svg.node().appendChild(this.g);
                console.log('Successfully added the <g> node to the map.');
            } else {
                console.error("The <g> node with id 'countries 1 1' was not found.");
            }

            // Create a zoom behavior
            this.zoom = d3
                .zoom()
                .scaleExtent([1, 8]) // Set the zoom limits
                .on('start', this.zoomStarted)
                .on('zoom', this.zoomed)
                .on('end', this.zoomEnded); // Attach the zoom event handler

            // Apply the zoom behavior to the SVG
            this.svg.call(this.zoom);

            // Handle continent clicks
            d3.selectAll('g.continent')
                .on('click', (event) => {
                    if (this.useIn === 'continent-picker') {
                        this.emitContinentClick(event);
                    }

                    if (this.shouldShowCountries) {
                        this.emitCountryClick(event);
                    } else {
                        this.zoomToContinent(event.target.parentElement.id);
                    }
                })
                .on('mouseover', (_) => {})
                .on('mousemove', (event) => {
                    this.highlightElement(event, true);
                    this.updateTooltip(event);
                })
                .on('mouseout', (event) => {
                    this.highlightElement(event, false);
                    this.tooltipVisible = false; // Hide tooltip
                });

            if (this.useIn === 'country-picker') {
                this.showCountries();
                this.zoomToContinent(this.selectedContinentIn, false);
            } else {
                this.showContinents();
            }
        },
        emitContinentClick(event) {
            const continentKey = event.target.parentElement.id;
            const currentContinentState = this.continents[continentKey]?.state;
            if (currentContinentState != null && currentContinentState !== 'blocked') {
                this.$emit('continent-tabbed', continentKey);
            }
        },
        emitCountryClick(event) {
            const countryCode = event.target.id;
            const currentCountryState = this.countries[countryCode]?.state;
            if (currentCountryState != null && currentCountryState !== 'blocked') {
                this.$emit('country-tabbed', countryCode);
            }
        },
        setupContinent() {
            this.$emit('setup-continent');
        },
        clearCountrySelection() {
            let assignmentName;

            for (let countryKey in this.countries) {
                if (this.countries[countryKey].state === 'selected') {
                    this.countries[countryKey].state = 'none';
                    this.countries[countryKey].statusColor = null;
                    assignmentName = this.countries[countryKey].assignmentName;
                    this.countries[countryKey].assignmentName = null;
                }
            }

            this.selectedCountry = null;

            return assignmentName;
        },
        selectNewCountry(countryCode, assignmentName) {
            this.countries[countryCode].state = 'selected';
            this.countries[countryCode].assignmentName = assignmentName;
            this.selectedCountry = this.countries[countryCode].countryName;
        },
        changeSelectedCountry(countryCode) {
            let previousSelectionAssignmentName = this.clearCountrySelection();

            if (countryCode) {
                this.selectNewCountry(countryCode, previousSelectionAssignmentName);
            }
            this.paintCountries();
        },
        clearContinentSelection() {
            let categoryName;
            let oldColor;
            for (let continentKey in this.continents) {
                if (this.continents[continentKey].state === 'selected') {
                    this.continents[continentKey].state = 'none';
                    oldColor = this.continents[continentKey]?.color ?? defaultSelectionColor;
                    this.continents[continentKey].color = null;
                    categoryName = this.continents[continentKey].categoryName;
                    this.continents[continentKey].categoryName = null;
                    break;
                }
            }

            this.selectedContinent = null;

            return { categoryName: categoryName, color: oldColor };
        },
        selectNewContinent(continentKey, color, categoryName) {
            this.continents[continentKey].state = 'selected';
            this.continents[continentKey].color = color;
            this.continents[continentKey].categoryName = categoryName;
            this.selectedContinent = this.continents[continentKey].continentName;
        },
        changeSelectedContinent(continentKey, color, categoryName) {
            let oldSelection = this.clearContinentSelection();

            if (continentKey) {
                this.selectNewContinent(
                    continentKey,
                    color ?? oldSelection?.color ?? defaultSelectionColor,
                    categoryName ?? oldSelection?.categoryName,
                );
            }

            this.showContinents();
        },
        createProgressBar(parentElement, centerX, centerY, data) {
            const barWidth = 75;
            const barHeight = 10;
            const total = d3.sum(data, (d) => d.count);

            // Translate the group to the specified centerX and centerY
            const group = parentElement.append('g').attr('class', 'progress-bar');

            // Create progress bar segments
            let xOffset = centerX - barWidth / 2;
            group
                .selectAll('rect')
                .data(data)
                .enter()
                .append('rect')
                .attr('x', (d, _) => {
                    const previousOffset = xOffset;
                    xOffset += (d.count / total) * barWidth;
                    return previousOffset;
                })
                .attr('y', centerY - barHeight / 2)
                .attr('width', (d) => (d.count / total) * barWidth)
                .attr('height', barHeight)
                .attr('fill', (d) => d.color);

            // Add labels inside the bars
            xOffset = centerX - barWidth / 2;
            group
                .selectAll('text')
                .data(data)
                .enter()
                .append('text')
                .attr('x', (d) => {
                    const center = xOffset + ((d.count / total) * barWidth) / 2;
                    xOffset += (d.count / total) * barWidth;
                    return center;
                })
                .attr('y', centerY + 2)
                .attr('text-anchor', 'middle')
                .attr('font-size', '6px')
                .attr('fill', 'white')
                .text((d) => (d.count !== 0 ? d.count : ''));
        },
        createCircularProgressBar(parentElement, centerX, centerY, data) {
            const total = d3.sum(data, (d) => d.count);

            const group = parentElement.append('g').attr('class', 'progress-bar');

            // Create circular progress bar segments
            let startAngle = 0;
            group
                .selectAll('.progress-bar path')
                .data(data)
                .enter()
                .append('path')
                .attr('d', (d) => {
                    const endAngle = startAngle + (d.count / total) * 2 * Math.PI;
                    const path = d3
                        .arc()
                        .innerRadius(0)
                        .outerRadius(circularProgressBarRadius)
                        .startAngle(startAngle)
                        .endAngle(endAngle);
                    startAngle = endAngle;
                    return path();
                })
                .attr('pointer-events', 'none')
                .attr('transform', `translate(${centerX}, ${centerY})`)
                .attr('fill', (d) => d.color);

            // Add counts inside the colored circle segments
            startAngle = 0;
            group
                .selectAll('text')
                .data(data)
                .enter()
                .append('text')
                .attr('pointer-events', 'none')
                .attr('transform', (d) => {
                    const midAngle = startAngle + (d.count / total) * Math.PI;
                    const x = (circularProgressBarRadius - 20) * Math.cos(midAngle - Math.PI / 2);
                    const y = (circularProgressBarRadius - 20) * Math.sin(midAngle - Math.PI / 2);
                    startAngle += (d.count / total) * 2 * Math.PI;
                    return `translate(${x}, ${y}) translate(${centerX}, ${centerY})`;
                })
                .attr('text-anchor', 'middle')
                .attr('font-size', '14px')
                .attr('font-weight', 'bold')
                .attr('fill', '#fff')
                .text((d) => (d.count !== 0 ? d.count : ''));
        },
        createCategoryLabel(parentElement, centerX, centerY, labelName) {
            const horizontalPadding = 20;
            const verticalPadding = 20;

            const group = parentElement.append('g').attr('class', 'category-label');

            const textElement = group
                .append('text')
                .attr('pointer-events', 'none')
                .attr(
                    'transform',
                    `translate(${centerX}, ${centerY - circularProgressBarRadius - verticalPadding * 2})`,
                )
                .attr('text-anchor', 'middle')
                .attr('alignment-baseline', 'central')
                .attr('font-size', '14px')
                .attr('fill', '#000')
                .text(labelName);

            const textBBox = textElement.node().getBBox();
            const rectWidth = textBBox.width + horizontalPadding * 2;
            const rectHeight = textBBox.height + verticalPadding * 2;
            group
                .append('rect')
                .lower()
                .attr('pointer-events', 'none')
                .attr('fill', 'var(--secondary-background-color)')
                .attr('rx', '12px')
                .attr('ry', '12px')
                .attr('stroke', 'var(--primary)')
                .attr('stroke-width', '1px')
                .attr('height', rectHeight)
                .attr('width', rectWidth)
                .attr(
                    'transform',
                    `translate(${centerX - rectWidth / 2}, ${centerY - circularProgressBarRadius - rectHeight / 2 - verticalPadding * 2})`,
                );
        },
        updateTooltip(event) {
            if (this.shouldShowCountries) {
                const countryCode = event.target?.id;
                this.tooltipContent =
                    this.countries[countryCode]?.assignmentName ??
                    (this.useIn === 'country-picker' && this.countries[countryCode]?.state === 'blocked'
                        ? 'Reeds gebruikt'
                        : (this.countries[countryCode]?.countryName ?? ''));
            } else {
                this.tooltipContent = null;
            }

            let leftPadding =
                this.useIn === 'country-picker' || this.useIn === 'continent-picker'
                    ? this.$refs.mapContainer.closest('.modal-dialog').getBoundingClientRect().x
                    : 0;
            let topPadding =
                this.useIn === 'country-picker' || this.useIn === 'continent-picker'
                    ? this.$refs.mapContainer.closest('.modal-dialog').getBoundingClientRect().y + 21 * 2 // TODO: we need to get rid of this magic number
                    : 0;

            if (this.tooltipContent != null && this.tooltipContent !== '') {
                this.tooltipVisible = true; // Show tooltip
                this.tooltipStyle.left = `${event.x + 10 - leftPadding}px`;
                this.tooltipStyle.top = `${event.y + 10 - topPadding}px`;
            }
        },
        resetHighlights() {
            d3.select(this.g).selectAll('path').style('filter', 'brightness(100%)');
        },
        highlightElement(event, shouldHighlight) {
            if (this.shouldShowCountries) {
                d3.select(event.target).style('filter', shouldHighlight ? 'brightness(110%)' : 'brightness(100%)');
            } else {
                d3.select(event.target.parentElement)
                    .selectAll('path')
                    .style('filter', shouldHighlight ? 'brightness(110%)' : 'brightness(100%)');
            }
        },
        zoomStarted(_) {},
        zoomed(event) {
            d3.select(this.g).attr('transform', event.transform);

            if (this.useIn === 'country-picker') {
                this.shouldShowCountries = true;
            } else if (this.useIn !== 'continent-picker') {
                this.shouldShowCountries = event.transform.k >= 1.5;
            }

            this.tooltipVisible = false;

            this.resetHighlights();
            if (this.shouldShowCountries) {
                this.showCountries();
            } else {
                this.showContinents();
            }

            d3.selectAll('.progress-bar').attr('transform', event.transform);
            d3.selectAll('.category-label').attr('transform', event.transform);
            d3.selectAll('.assignment-indicator-status').attr('transform', event.transform);
        },
        zoomEnded(_) {},
        removeAllIndicators() {
            const container = d3.select(this.$refs.mapContainer);
            container.selectAll('.progress-bar').remove();
            container.selectAll('.category-label').remove();
        },
        addCategoryProgressBarsAndLabels() {
            d3.selectAll('g.continent').each((_, i, nodes) => {
                const continentKey = d3.select(nodes[i]).attr('id');
                const statusData = this.continents[continentKey]?.statusData;
                const label = this.continents[continentKey]?.categoryName;

                const bbox = nodes[i].getBBox();
                const centerX = bbox.x + bbox.width / 2;
                const centerY = bbox.y + bbox.height / 2;

                if (statusData != null) {
                    this.createCircularProgressBar(this.svg, centerX, centerY, statusData);
                    //this.createProgressBar(this.svg, centerX, centerY, statusData);
                }

                if (label != null) {
                    this.createCategoryLabel(this.svg, centerX, centerY, label);
                }
            });
        },
        zoomToContinent(continentId, withTransition = true) {
            // Select the tapped continent <g> by its ID
            const selectedContinent = d3.select(`#${continentId}`);

            // Get the bounding box of the selected continent
            const bbox = selectedContinent.node().getBBox();

            // Calculate the center of the bounding box
            const centerX = bbox.x + bbox.width / 2;
            const centerY = bbox.y + bbox.height / 2;

            const scale = 1.8;

            // Calculate the translation to center the area in the viewport
            const translateX = this.viewportWidth / 2 - centerX * scale;
            const translateY = this.viewportHeight / 2 - centerY * scale;

            // Apply the zoom transformation with a smooth transition
            if (withTransition) {
                this.svg
                    .transition()
                    .duration(750) // Animation duration in milliseconds
                    .call(
                        this.zoom.transform, // Apply zoom behavior's transform method
                        d3.zoomIdentity.translate(translateX, translateY).scale(scale),
                    );
            } else {
                this.svg.call(
                    this.zoom.transform, // Apply zoom behavior's transform method
                    d3.zoomIdentity.translate(translateX, translateY).scale(scale),
                );
            }
        },
        showContinents() {
            this.removeAllIndicators();
            this.paintContinents();
            this.addCategoryProgressBarsAndLabels();
        },
        paintContinents() {
            d3.selectAll('g.continent').each((_, i, nodes) => {
                const continentKey = d3.select(nodes[i]).attr('id');

                const color =
                    this.useIn === 'continent-picker' && this.continents[continentKey].state === 'selected'
                        ? this.continents[continentKey]?.color
                        : this.continents[continentKey]?.state === 'blocked'
                          ? 'darkGrey'
                          : this.continents[continentKey]?.color || unselectedMapElementColor;

                if (this.continents[continentKey]?.state === 'selected') {
                    this.selectedContinent = this.continents[continentKey].continentName;
                }

                d3.select(nodes[i])
                    .selectAll('path')
                    .attr('fill', color)
                    .attr('stroke', color)
                    .attr('stroke-width', '1');
            });
        },
        showCountries() {
            this.removeAllIndicators();
            this.paintCountries();
        },
        paintCountries() {
            d3.selectAll('g.continent')
                .selectAll('path')
                .each((_, i, nodes) => {
                    const countryCode = d3.select(nodes[i]).attr('id');
                    const color =
                        this.useIn === 'country-picker' && this.countries[countryCode]?.state === 'selected'
                            ? (this.countries[countryCode]?.statusColor ?? defaultSelectionColor)
                            : this.countries[countryCode]?.state === 'blocked'
                              ? 'darkGrey'
                              : this.countries[countryCode]?.statusColor || unselectedMapElementColor;

                    if (this.countries[countryCode]?.state === 'selected') {
                        this.selectedCountry = this.countries[countryCode].countryName;
                    }

                    d3.select(nodes[i]).attr('fill', color).attr('stroke', 'white').attr('stroke-width', '0.2');
                });
        },
    },
    computed: {
        canDisplayMap() {
            return !(this.useIn === 'country-picker' && this.selectedContinentIn == null);
        },
    },
    watch: {
        canDisplayMap(newVal, _) {
            if (newVal) {
                this.initializeMap();
            }
        },
    },
};
</script>

<style scoped lang="scss">
html,
body,
.map-container,
svg {
    width: 100%;
    height: 600px;
    //width: 500px;
    //height: 200px;
    margin: 0;
    padding: 0;
    overflow: hidden;
}

.map-container {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: $border-radius-content;
    border: 1px solid lightgrey;
}

.tooltip {
    opacity: 0;
    position: absolute;
    transition: opacity 0.3s;
    pointer-events: none;
}

.tooltip.visible {
    opacity: 1;
}

g .continent:hover {
    opacity: 0.8;
    transition: 0.2s;
    cursor: pointer;
}

path.country.visible {
    visibility: visible;
}

g rect:hover {
    opacity: 0.5;
    transition: 0.6s;
    cursor: pointer;
}

::v-deep .map-tooltip {
    padding: map-get($grid-gutter-spacing, 'fifteen');
    background-color: $secondary-background-color;
    border-radius: $border-radius-content;
    border: 1px solid $primary;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}

.progress-bar {
    font-size: small;
}

.map-selection {
    margin-bottom: $grid-gutter-width;
}
</style>
